import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, Circle, ChevronRight, ChevronLeft, Lock, BookOpen } from "lucide-react";

function extractYTId(url) {
    if (!url) return null;
    const m = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/);
    return m ? m[1] : null;
}

export default function LessonPlayer() {
    const { courseId } = useParams();
    const [course, setCourse] = useState(null);
    const [active, setActive] = useState(null); // lesson id

    const load = async () => {
        const { data } = await api.get(`/courses/${courseId}`);
        setCourse(data);
        if (!active) {
            const firstLesson = data.modules.flatMap(m => m.lessons || [])[0];
            if (firstLesson) setActive(firstLesson.id);
        }
    };
    useEffect(() => { load(); }, [courseId]); // eslint-disable-line

    const flat = useMemo(() => course ? course.modules.flatMap(m => m.lessons.map(l => ({ ...l, _moduleTitle: m.title }))) : [], [course]);
    const currentIdx = flat.findIndex(l => l.id === active);
    const current = flat[currentIdx];

    const markComplete = async () => {
        if (!current) return;
        await api.post(`/lessons/${current.id}/complete`);
        toast.success("Lesson marked complete");
        await load();
        if (currentIdx < flat.length - 1) setActive(flat[currentIdx + 1].id);
    };

    if (!course) return <Skeleton className="h-screen" />;

    return (
        <div className="-m-4 lg:-m-8 grid lg:grid-cols-[320px_1fr] min-h-[calc(100vh-4rem)]" data-testid="lesson-player">
            {/* Curriculum */}
            <aside className="border-r border-border bg-card overflow-auto scrollbar-thin">
                <div className="p-5 border-b border-border">
                    <Link to="/student/courses" className="text-xs text-muted-foreground hover:text-foreground"><ChevronLeft className="h-3.5 w-3.5 inline" /> Back to courses</Link>
                    <h2 className="font-bold mt-2 line-clamp-2">{course.title}</h2>
                    {course.progress && (
                        <div className="mt-3">
                            <div className="flex justify-between text-xs"><span className="text-muted-foreground">Progress</span><span className="font-bold">{course.progress.percent}%</span></div>
                            <div className="mt-1.5 h-2 bg-muted rounded-full overflow-hidden">
                                <div className="h-full bg-brand-gradient transition-all" style={{ width: `${course.progress.percent}%` }} />
                            </div>
                        </div>
                    )}
                </div>
                <div className="p-3">
                    {course.modules.map((m, mi) => (
                        <div key={m.id} className="mb-4" data-testid={`module-tree-${m.id}`}>
                            <div className="px-2 mb-1.5">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Module {mi + 1}</div>
                                <div className="font-bold text-sm mt-0.5">{m.title}</div>
                            </div>
                            <ul>
                                {m.lessons.map(l => {
                                    const done = l.progress?.completed;
                                    const isActive = active === l.id;
                                    return (
                                        <li key={l.id}>
                                            <button onClick={() => setActive(l.id)} data-testid={`lesson-item-${l.id}`} className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left text-sm transition-all ${isActive ? "bg-[#F0EFFF] dark:bg-[#1C1A2E] text-[#726AF0]" : "hover:bg-muted"}`}>
                                                {done ? <CheckCircle2 className="h-4 w-4 text-[#16A34A] shrink-0" /> : <Circle className="h-4 w-4 text-muted-foreground shrink-0" />}
                                                <span className="flex-1 line-clamp-1">{l.title}</span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>
            </aside>

            {/* Main */}
            <main className="flex flex-col">
                {current ? (
                    <>
                        <div className="p-6 lg:p-10 flex-1 overflow-auto">
                            <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{current._moduleTitle}</div>
                            <h1 className="font-display text-3xl lg:text-4xl mt-1.5">{current.title}</h1>
                            {extractYTId(current.youtube_url) && (
                                <div className="mt-6 aspect-video rounded-2xl overflow-hidden border border-border bg-black shadow-mx-md">
                                    <iframe
                                        src={`https://www.youtube-nocookie.com/embed/${extractYTId(current.youtube_url)}`}
                                        title={current.title}
                                        allowFullScreen
                                        className="w-full h-full"
                                        data-testid="lesson-video"
                                    />
                                </div>
                            )}
                            {current.notes && (
                                <div className="mt-8 max-w-3xl">
                                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2 flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" /> Notes</div>
                                    <div className="text-base leading-relaxed whitespace-pre-wrap">{current.notes}</div>
                                </div>
                            )}
                        </div>
                        <div className="sticky bottom-0 border-t border-border bg-card p-4 flex items-center justify-between gap-3" data-testid="lesson-action-bar">
                            <Button variant="outline" disabled={currentIdx === 0} onClick={() => setActive(flat[currentIdx - 1].id)} className="rounded-xl" data-testid="prev-lesson-btn">
                                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                            </Button>
                            <Button onClick={markComplete} className="mx-btn-primary" data-testid="mark-complete-btn">
                                {current.progress?.completed ? "Next lesson" : "Mark complete & continue"} <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                        <Lock className="h-4 w-4 mr-2" /> No lessons in this course yet.
                    </div>
                )}
            </main>
        </div>
    );
}
